import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { healthProjects } from "../data/projects";
import aboutPhoto from "../assets/ellen-about.jpg";

const FACTS = [
  { label: "Based In", value: "Kumasi, Ghana" },
  { label: "Credential", value: "BSc. Disability & Rehabilitation Studies" },
  { label: "Currently", value: "Rehabilitation Specialist Assistant, Scrum Master & Accessibility Engineer (UX Researcher)" },
];

const STAT_STRIP = [
  { value: "3+", label: "Years of Experience" },
  { value: "3", label: "Research Projects" },
  { value: "6", label: "Certifications" },
  { value: "3+", label: "Dev Projects" },
];

const HOW_IT_SHOWS_UP = [
  {
    label: "Permanent",
    desc: "Present from birth, or acquired through illness, injury, or disability: the person most of us picture first.",
  },
  {
    label: "Temporary",
    desc: "Recovering from surgery, a broken limb, an acute illness: weeks or months where the world needs to work differently.",
  },
  {
    label: "Situational",
    desc: "Carrying a child, holding groceries in one arm, standing in a crowded room, or simply getting older and finding stairs less forgiving.",
  },
];

const SKILL_GROUPS = [
  {
    category: "Web Development",
    items: ["HTML", "CSS", "JavaScript", "Responsive design"],
  },
  {
    category: "Human-Centred & Accessible Design",
    items: ["Figma", "User research", "Usability testing", "WCAG 2.2", "UDL", "Wireframing & prototyping"],
  },
  {
    category: "Agile & Product Delivery",
    items: ["Scrum facilitation", "Sprint & backlog planning", "Stakeholder communication", "Velocity & delivery forecasting"],
  },
  {
    category: "Data & Research",
    items: ["Python (basic)", "Excel", "Data cleaning", "Statistical modeling", "Biostatistics"],
  },
  {
    category: "Rehabilitation & Disability Expertise",
    items: ["Mobility assessment", "Patient-centred communication", "Accessibility auditing"],
  },
];

const EXPERIENCE = [
  {
    date: "Oct 2023 – Present",
    org: "Kidney Specialist Center, Ghana",
    role: "Rehabilitation Specialist Assistant",
    bullets: [
      "Provide rehabilitation support to patients with kidney-related and other chronic health conditions.",
      "Assist in patient assessments, therapeutic exercises, and monitoring treatment progress.",
      "Educate patients on lifestyle modifications, ergonomics, and safe rehabilitation practices.",
      "Collaborate with multidisciplinary teams to implement individualised patient care plans.",
    ],
  },
  {
    date: "Jun – Aug 2024",
    org: "Physiotherapy & Audiology Unit, KATH",
    role: "Clinical & Community Attachment Intern",
    bullets: [
      "Assisted physiotherapists and audiologists in assessment and rehabilitation of patients with physical and sensory disabilities.",
      "Conducted environmental and occupational health evaluations in clinical settings.",
      "Educated patients on injury prevention, ergonomics, and safe rehabilitation practices.",
      "Participated in multidisciplinary care and rehabilitation planning.",
    ],
  },
];

const RESEARCH_INTERESTS = [
  "Accessibility in Design Thinking", "Assistive Technology in Education",
  "Agile / Scrum Product Delivery", "Disability & Education",
];

const COURSES = [
  { platform: "Google", name: "Google UX Design Professional Certificate" },
  { platform: "Arizona State University", name: "Human Factors & Usability Engineering: Designing for Humans Specialization" },
  { platform: "Arizona State University", name: "Technology & Entrepreneurship Specialization" },
  { platform: "Arizona State University", name: "Professional Skills: Workplace Communication Specialization" },
  { platform: "Baobab · Mastercard Foundation", name: "What is Transformative Leadership?" },
  { platform: "Baobab · Arizona State University", name: "Intermediate Python for Data Analysis" },
];

export default function Home() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [hash]);

  return (
    <>
      <Nav />

      {/* HERO */}
      <section className="mx-auto max-w-content px-6 pb-16 pt-32 md:px-12 md:pb-24 md:pt-40">
        <div className="grid gap-14 md:grid-cols-[1.3fr_0.9fr] md:gap-10">
          <Reveal>
            <h1 className="font-serif text-[3rem] font-extrabold leading-[1.05] text-heading sm:text-[3.75rem] lg:text-[4.5rem]">
              Ellen Boakye
            </h1>
            <p className="mt-3 font-serif text-lg italic text-gold-700 md:text-xl">
              Rehabilitation Specialist turned Accessibility Engineer.
            </p>
            <p className="mt-5 max-w-[48ch] text-[1.05rem] leading-relaxed text-ink-soft">
              I worked in rehabilitation clinics before I started writing code. Most of what I build now
              comes directly out of that time.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
              <Link
                to="/projects"
                className="border border-forest-900 bg-forest-900 px-6 py-3 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
              >
                View Projects
              </Link>
              <Link
                to={{ pathname: "/", hash: "#contact" }}
                className="text-[0.75rem] uppercase tracking-widest2 text-ink-soft transition-colors hover:text-heading"
              >
                Get in touch &rarr;
              </Link>
            </div>
          </Reveal>

          <Reveal delay={120} className="md:pt-2">
            <p className="eyebrow mb-6">At a Glance</p>
            <dl className="space-y-5">
              {FACTS.map((f) => (
                <div key={f.label} className="border-t border-line pt-5 first:border-t-0 first:pt-0">
                  <dt className="text-[0.75rem] uppercase tracking-widest2 text-ink-faint">{f.label}</dt>
                  <dd className="mt-1.5 font-serif text-[1.05rem] text-heading">{f.value}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      {/* STAT STRIP */}
      <Reveal as="section" className="border-y border-line bg-surface-alt">
        <div className="mx-auto grid max-w-content grid-cols-2 divide-x divide-line px-6 sm:grid-cols-4 md:px-12">
          {STAT_STRIP.map((s) => (
            <div key={s.label} className="px-2 py-8 text-center first:pl-0 sm:px-6">
              <div className="font-serif text-3xl text-heading">{s.value}</div>
              <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">{s.label}</div>
            </div>
          ))}
        </div>
      </Reveal>

      {/* WHY THIS WORK: mission / motivation */}
      <section className="bg-forest-900">
        <div className="mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
          <Reveal>
            <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-300">Why This Work</p>
            <h2 className="mt-3 max-w-[24ch] font-serif text-3xl font-extrabold leading-tight text-white md:text-4xl">
              The Room You Might Need Someday
            </h2>
          </Reveal>

          <div className="mt-10 grid gap-10 md:grid-cols-2 md:gap-16">
            <Reveal className="space-y-5 text-[0.98rem] leading-[1.85] text-forest-50/85">
              <p>
                I spent my first years out of school in rehab clinics, watching people relearn how to walk,
                how to grip a pen, how to trust their own body again. Only a handful of them were born with a
                disability. Most acquired it along the way: a workplace injury, a stroke, a road accident, or
                simply the years wearing a body down.
              </p>
              <p>
                That&rsquo;s the thing people rarely say about disability: for most of us it isn&rsquo;t a
                fixed category you&rsquo;re born into or spared from. It&rsquo;s a door. Some walk through it
                slowly, as arthritis or a bad knee or the years catch up. Some get pushed through in a single
                afternoon. I stopped believing there&rsquo;s a &ldquo;them&rdquo; in this conversation a long
                time ago. There&rsquo;s only us, on different timelines.
              </p>
            </Reveal>
            <Reveal delay={100} className="space-y-5 text-[0.98rem] leading-[1.85] text-forest-50/85">
              <p>
                So no, I don&rsquo;t build accessible things because it&rsquo;s the compliant thing to do, or
                the nice thing to do. I build them because I&rsquo;ve sat with people <em className="italic text-white">the day after</em> the
                door opened, and I know exactly how much it costs to have nothing waiting on the other side.
              </p>
              <p>
                Extablify exists because a kid with low vision in a Ghanaian classroom shouldn&rsquo;t have
                to teach himself to read a screen alone. KASA exists because a child&rsquo;s whole care
                history shouldn&rsquo;t live in a notebook that gets left behind. Every certification below is
                one more thing I picked up on the way to building work that actually holds.
              </p>
            </Reveal>
          </div>

          <Reveal delay={150} className="mt-16 border-t border-white/15 pt-10">
            <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-300">
              How Mobility Challenges Actually Show Up
            </p>
            <div className="mt-6 grid gap-px border-y border-white/15 sm:grid-cols-3">
              {HOW_IT_SHOWS_UP.map((h, i) => (
                <div
                  key={h.label}
                  className={`border-t border-white/15 py-6 sm:border-t-0 ${i === 0 ? "sm:pr-6" : i === 2 ? "sm:pl-6" : "sm:px-6"}`}
                >
                  <div className="font-serif text-xl text-white">{h.label}</div>
                  <p className="mt-2 text-[0.85rem] leading-relaxed text-forest-50/75">{h.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CREDENTIALS, elevated */}
      <section className="border-b border-line bg-surface-alt">
        <div className="mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
          <div className="flex items-baseline gap-4">
            <span className="font-serif text-sm text-ink-faint">01</span>
            <p className="eyebrow">Credentials</p>
          </div>

          <div className="mt-8 grid gap-px border border-line bg-line">
            <Reveal as={Link} to="/certificates" className="group bg-surface p-8 transition-colors hover:bg-surface-alt">
              <h3 className="font-serif text-xl text-heading">Certificates</h3>
              <p className="mt-3 max-w-[42ch] text-[0.9rem] leading-relaxed text-ink-soft">
                Every certification I&rsquo;ve earned, with a scan of each.
              </p>
              <span className="mt-5 inline-block text-[0.75rem] uppercase tracking-widest2 text-link group-hover:text-heading">
                View certificates &rarr;
              </span>
            </Reveal>
            {/* CV card hidden
            <Reveal as={Link} to="/cv" delay={80} className="group bg-surface p-8 transition-colors hover:bg-surface-alt">
              <h3 className="font-serif text-xl text-heading">CV</h3>
              <p className="mt-3 max-w-[42ch] text-[0.9rem] leading-relaxed text-ink-soft">
                Read it page by page, or download the PDF.
              </p>
              <span className="mt-5 inline-block text-[0.75rem] uppercase tracking-widest2 text-link group-hover:text-heading">
                View CV &rarr;
              </span>
            </Reveal>
            */}
          </div>

          <div className="mt-16">
            <h3 className="font-serif text-2xl font-extrabold text-heading">Certifications &amp; Courses</h3>
            <div className="mt-8 grid divide-y divide-line border-t border-line sm:grid-cols-2 sm:divide-x sm:divide-y-0">
              {COURSES.map((c, i) => (
                <Reveal
                  key={c.name}
                  delay={i * 30}
                  className="px-0 py-4 sm:px-6 sm:first:pl-0"
                >
                  <p className="text-[0.9rem] text-ink-soft">{c.name}</p>
                  <p className="mt-1 text-[0.72rem] uppercase tracking-widest2 text-ink-faint">{c.platform}</p>
                </Reveal>
              ))}
            </div>
            <Link
              to="/certificates"
              className="mt-6 inline-block text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading"
            >
              See all certifications &amp; courses &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* PROJECTS PREVIEW */}
      <section id="projects" className="mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
        <div className="mb-12 flex items-baseline gap-4">
          <span className="font-serif text-sm text-ink-faint">02</span>
          <div>
            <p className="eyebrow">Selected Work</p>
            <h2 className="mt-2 font-serif text-3xl font-extrabold text-heading md:text-4xl">Featured Projects</h2>
          </div>
        </div>

        <div className="divide-y divide-line border-t border-line">
          {healthProjects.map((p, i) => (
            <Reveal key={p.title} delay={i * 70} className="grid gap-2 py-8 md:grid-cols-[1fr_2fr] md:gap-10">
              <div>
                <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-500">{p.tag}</p>
                <p className="mt-2 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">{p.foot}</p>
              </div>
              <div>
                <h3 className="font-serif text-xl text-heading">{p.title}</h3>
                <p className="mt-2 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">{p.desc}</p>
                {p.detailPath && (
                  <Link
                    to={p.detailPath}
                    className="mt-4 inline-block text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading"
                  >
                    Read more &rarr;
                  </Link>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-10">
          <Link to="/projects" className="text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading">
            Browse all projects &rarr;
          </Link>
        </div>
      </section>

      {/* SKILLS */}
      <section id="skills" className="border-y border-line bg-surface-alt">
        <div className="mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
          <div className="mb-12 flex items-baseline gap-4">
            <span className="font-serif text-sm text-ink-faint">03</span>
            <div>
              <p className="eyebrow">Expertise</p>
              <h2 className="mt-2 font-serif text-3xl font-extrabold text-heading md:text-4xl">Skills &amp; Technologies</h2>
            </div>
          </div>

          <div className="grid gap-x-10 gap-y-8 md:grid-cols-2">
            {SKILL_GROUPS.map((g, i) => (
              <Reveal key={g.category} delay={i * 50} className="border-t border-line pt-5">
                <h3 className="font-serif text-base text-heading">{g.category}</h3>
                <p className="mt-2 text-[0.88rem] leading-relaxed text-ink-soft">{g.items.join(" · ")}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section id="experience" className="mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
        <div className="mb-12 flex items-baseline gap-4">
          <span className="font-serif text-sm text-ink-faint">04</span>
          <div>
            <p className="eyebrow">Career</p>
            <h2 className="mt-2 font-serif text-3xl font-extrabold text-heading md:text-4xl">Work &amp; Research Experience</h2>
          </div>
        </div>

        <div className="border-t border-line">
          {EXPERIENCE.map((e) => (
            <Reveal
              as="div"
              key={e.role}
              className="grid gap-3 border-b border-line py-9 md:grid-cols-[220px_1fr] md:gap-10"
            >
              <div>
                <div className="text-[0.75rem] uppercase tracking-widest2 text-gold-500">{e.date}</div>
                <div className="mt-1 text-[0.85rem] text-ink-soft">{e.org}</div>
              </div>
              <div>
                <h3 className="font-serif text-lg text-heading">{e.role}</h3>
                <ul className="mt-3 space-y-2">
                  {e.bullets.map((b) => (
                    <li key={b} className="flex gap-3 text-[0.9rem] leading-relaxed text-ink-soft">
                      <span className="mt-[0.55em] h-1 w-1 flex-shrink-0 rounded-full bg-gold-500" aria-hidden="true" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="border-y border-line bg-surface-alt">
        <div className="mx-auto grid max-w-content gap-14 px-6 py-20 md:grid-cols-[0.85fr_1.15fr] md:gap-16 md:px-12 md:py-28">
          <Reveal className="flex justify-center md:justify-start">
            <div className="relative">
              <img
                src={aboutPhoto}
                alt="Ellen Boakye"
                className="h-64 w-64 rounded-full object-cover object-center shadow-sm sm:h-80 sm:w-80"
              />
              <div className="pointer-events-none absolute -bottom-3 -right-3 -z-10 h-full w-full rounded-full border border-gold-300" aria-hidden="true" />
            </div>
          </Reveal>
          <Reveal delay={100}>
            <p className="eyebrow">About Me</p>
            <h2 className="mt-2 font-serif text-3xl font-extrabold text-heading md:text-4xl">
              Accessibility Developer &amp; Rehab Specialist
            </h2>
            <p className="mt-6 max-w-[56ch] text-[0.95rem] leading-[1.9] text-ink-soft">
              It started as one course at KNUST, Disability and Education, meant to be a single module
              among many. It didn&rsquo;t stay one. The day school ended, I went looking for what that
              course had only pointed at, sitting with someone who couldn&rsquo;t get past a login form,
              watching a student hunt for a button that wasn&rsquo;t there. Everything I&rsquo;ve built
              since has been that course, followed all the way through.
            </p>
            <p className="mt-5 max-w-[56ch] text-[0.95rem] leading-[1.9] text-ink-soft">
              I run sprints as a Scrum Master at SecureTags Technologies by day, and at night I build the
              things nobody asked me to build, a browser extension, a platform for special needs schools,
              tools designed to work even when the internet doesn&rsquo;t.
            </p>

            <dl className="mt-9 border-t border-line pt-6">
              <dt className="text-[0.75rem] uppercase tracking-widest2 text-gold-500">Education</dt>
              <dd className="mt-2 font-serif text-lg text-heading">BSc. Disability and Rehabilitation Studies</dd>
              <dd className="mt-1 text-[0.85rem] text-ink-soft">
                Kwame Nkrumah University of Science and Technology (KNUST) &middot; Aug 2023
              </dd>
            </dl>

            <div className="mt-7 border-t border-line pt-6">
              <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-500">Research Interests</p>
              <p className="mt-2 text-[0.88rem] leading-relaxed text-ink-soft">{RESEARCH_INTERESTS.join(" · ")}</p>
            </div>

            <div className="mt-7 border-t border-line pt-6">
              <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-500">Professional Memberships</p>
              <p className="mt-2 text-[0.88rem] leading-relaxed text-ink-soft">
                Disability &amp; Rehabilitation Associations &middot; Red Cross
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-forest-900">
        <div className="mx-auto max-w-content px-6 py-20 text-center md:px-12 md:py-28">
          <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-300">Let&rsquo;s Connect</p>
          <h2 className="mt-3 font-serif text-3xl font-extrabold text-white md:text-4xl">Get in Touch</h2>
          <p className="mx-auto mt-5 max-w-[48ch] text-[0.95rem] leading-relaxed text-forest-50/80">
            If you&rsquo;re working on something that needs a clinical eye and code in the same room, I want
            to hear about it: research, consultancy, clinical work, or a build that needs doing right.
          </p>

          <div className="mx-auto mt-12 grid max-w-3xl grid-cols-1 divide-y divide-white/15 border-y border-white/15 sm:grid-cols-[1.4fr_1fr_1.1fr] sm:divide-x sm:divide-y-0">
            <div className="px-0 py-5 sm:px-6 sm:first:pl-0">
              <div className="text-[0.75rem] uppercase tracking-widest2 text-gold-300">Email</div>
              <a href="mailto:ellenboakye585@gmail.com" className="mt-1 block whitespace-nowrap text-[0.9rem] text-white hover:text-gold-300">
                ellenboakye585@gmail.com
              </a>
            </div>
            <div className="px-0 py-5 sm:px-6">
              <div className="text-[0.75rem] uppercase tracking-widest2 text-gold-300">Phone</div>
              <a href="tel:+233247390102" className="mt-1 block whitespace-nowrap text-[0.9rem] text-white hover:text-gold-300">
                +233 24 739 0102
              </a>
            </div>
            <div className="px-0 py-5 sm:px-6 sm:last:pr-0">
              <div className="text-[0.75rem] uppercase tracking-widest2 text-gold-300">Website</div>
              <a href="https://ellenboakye.online" target="_blank" rel="noreferrer" className="mt-1 block whitespace-nowrap text-[0.9rem] text-white hover:text-gold-300">
                ellenboakye.online
              </a>
            </div>
          </div>

          <a
            href="mailto:ellenboakye585@gmail.com"
            className="mt-12 inline-block border border-white px-7 py-3 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-white hover:text-forest-900"
          >
            Send a Message
          </a>
        </div>
      </section>

      <Footer />
    </>
  );
}
